import { DenyBidValidationSchema } from "@next-pontos/validations";
import { useFormik } from "formik";
import { enqueueSnackbar } from "notistack";
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Grid, Layout, Paper } from "../../components";
import AuthContext from "../../context/auth";
import { useAxios, useGridUrlState, useToggle } from "../../hooks";
import { AccountModalBids } from "./AccountModal";
import { DeleteBidsModal } from "./DeleteModal";
import { DenyBidsModal } from "./DenyModal";
import { BidsTable } from "./Table";
import { Box, Tab, Tabs } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import type { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";

type TabKey = "ready" | "waiting" | "all";

const DEFAULT_TAB: TabKey = "ready";

const DEFAULT_PAGINATION = { page: 0, pageSize: 100 };
const DEFAULT_SORT: GridSortModel = [{ field: "priority", sort: "desc" }];
const DEFAULT_FILTER: GridFilterModel = { items: [] };

function a11yProps(index: number) {
    return {
        id: `bids-tab-${index}`,
        'aria-controls': `bids-tabpanel-${index}`,
    };
}

const isTabKey = (value: string | null): value is TabKey =>
    value === "ready" || value === "waiting" || value === "all";

export const Bids = () => {
    const { me } = useContext(AuthContext);
    const isAdmin = me?.role === "ADMIN";
    const canAccept = isAdmin || me?.role === "SELLER";

    const [searchParams, setSearchParams] = useSearchParams();

    const tabFromUrl = searchParams.get("tab");
    const tabKey: TabKey = isTabKey(tabFromUrl) ? tabFromUrl : DEFAULT_TAB;
    // Users without accept permission can't access the "waiting" tab; fall back to default if asked.
    const effectiveTabKey: TabKey = tabKey === "waiting" && !canAccept ? DEFAULT_TAB : tabKey;

    const handleTabChange = useCallback(
        (_: unknown, value: number) => {
            const withWaitingMap: TabKey[] = ["ready", "waiting", "all"];
            const withoutWaitingMap: TabKey[] = ["ready", "all"];
            const map = canAccept ? withWaitingMap : withoutWaitingMap;
            const next = map[value] ?? DEFAULT_TAB;
            setSearchParams(
                (prev) => {
                    const merged = new URLSearchParams(prev);
                    if (next === DEFAULT_TAB) merged.delete("tab");
                    else merged.set("tab", next);
                    // Reset pagination on tab change.
                    merged.delete("page");
                    return merged;
                },
                { replace: true },
            );
        },
        [canAccept, setSearchParams],
    );

    const tabIndex = useMemo(() => {
        if (canAccept) {
            return effectiveTabKey === "ready" ? 0 : effectiveTabKey === "waiting" ? 1 : 2;
        }
        return effectiveTabKey === "ready" ? 0 : 1;
    }, [canAccept, effectiveTabKey]);

    return (
        <Layout>
            <Grid container justifyContent="center" padding={3} gap={3}>
                <Box sx={{ width: "100%" }}>
                    <Paper>
                        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                            <Tabs value={tabIndex} onChange={handleTabChange}>
                                <Tab label="Prontas para Emitir" {...a11yProps(0)} />
                                {canAccept && <Tab label="Em Preparação" {...a11yProps(1)} />}
                                <Tab label="Todas" {...a11yProps(canAccept ? 2 : 1)} />
                            </Tabs>
                        </Box>
                    </Paper>
                    <BidsListTab tabKey={effectiveTabKey} />
                </Box>
            </Grid>
        </Layout>
    );
};

interface BidsListTabProps {
    tabKey: TabKey;
}

function BidsListTab({ tabKey }: BidsListTabProps) {
    const { me } = useContext(AuthContext);

    const {
        paginationModel,
        sortModel,
        filterModel,
        setPaginationModel,
        setSortModel,
        setFilterModel,
        apiQueryParams,
        searchParams,
        updateParams,
    } = useGridUrlState({
        defaultPaginationModel: DEFAULT_PAGINATION,
        defaultSortModel: DEFAULT_SORT,
        defaultFilterModel: DEFAULT_FILTER,
    });

    const selectedCompanyValues = useMemo<string[]>(() => {
        const raw = searchParams.get("companies");
        if (!raw) return [];
        return raw.split(",").map((c) => c.trim()).filter(Boolean);
    }, [searchParams]);

    const setSelectedCompanyValues = useCallback(
        (values: string[]) => {
            updateParams((next) => {
                if (!values.length) next.delete("companies");
                else next.set("companies", values.join(","));
                next.delete("page");
            });
        },
        [updateParams],
    );

    const bidsUrl = useMemo(() => {
        const params = new URLSearchParams(apiQueryParams);
        if (tabKey === "ready") params.set("ready_to_order", "true");
        else if (tabKey === "waiting") params.set("ready_to_order", "false");
        if (selectedCompanyValues.length) {
            params.set("companies", selectedCompanyValues.join(","));
        }
        return `/bid?${params.toString()}`;
    }, [apiQueryParams, selectedCompanyValues, tabKey]);

    const [bids, refetchBids] = useAxios(bidsUrl, { autoCancel: true });

    const [, postAPI] = useAxios({ method: "POST" }, { manual: true });
    const [bid, fetchBid] = useAxios("/bid", { manual: true });
    const [, deleteBid] = useAxios("/bid", { manual: true });

    const [deleteModalVisibility, deleteModalVisibilityControls] = useToggle(false);
    const [denyModalVisibility, denyModalVisibilityControls] = useToggle(false);
    const [accountModalVisibility, accountModalVisibilityControls] = useToggle(false);
    const [denyBidId, setDenyBidId] = useState<undefined | string | number>();
    const [deleteBidId, setDeleteBidId] = useState<undefined | string | number>();

    const denyFormik = useFormik({
        initialValues: {
            reason: "",
            send_email: true,
        },
        validationSchema: DenyBidValidationSchema,
        validateOnChange: true,
        validateOnMount: true,
        onSubmit: async (values) => {
            await postAPI({
                url: `/bid/${denyBidId}/deny`,
                data: values,
            });

            denyModalVisibilityControls.setFalse();
            refetchBids();
        },
    });

    const handleDeleteBid = async () => {
        const { status } = await deleteBid({
            url: `/bid/${deleteBidId}`,
            method: "DELETE",
        });

        if (status <= 299) {
            enqueueSnackbar("Oferta excluída com sucesso!", { variant: "success" });
        }

        deleteModalVisibilityControls.setFalse();
        refetchBids();
    };

    useEffect(() => {
        if (!denyModalVisibility) {
            setDenyBidId(undefined);
        }
    }, [denyModalVisibility]);

    const bidsRows: any[] = bids?.data?.rows || [];
    const hasOnOrder = bidsRows.some((item) => item.status === "ON_ORDER");
    const rowCount = bids?.data?.total ?? 0;

    return (
        <Fragment>
            <DenyBidsModal
                denyModalVisibility={denyModalVisibility}
                formik={denyFormik}
                modalVisibilityControls={denyModalVisibilityControls}
            />
            <DeleteBidsModal
                modalVisibility={deleteModalVisibility}
                modalVisibilityControls={() => {
                    deleteModalVisibilityControls.setFalse();
                    setDeleteBidId("");
                }}
                onConfirm={handleDeleteBid}
            />
            <AccountModalBids
                accountModalVisibility={accountModalVisibility}
                accountModalVisibilityControls={accountModalVisibilityControls}
                bid={bid.data}
                loading={bid.loading}
                onAccountSaved={() => {
                    if (bid.data?.id) {
                        fetchBid({ url: `/bid/${bid.data.id}` });
                    }
                }}
            />
            <Grid container justifyContent="center" padding={3} gap={2}>
                <Grid item xs={12}>
                    {hasOnOrder && (
                        <Alert color="warning" icon={false} sx={{ marginBottom: 2 }}>
                            <strong>Você possui ofertas em emissão!</strong>
                            <br />
                            Não se esqueça de finalizar a oferta quando não houverem mais emissões a serem feitas.
                        </Alert>
                    )}
                    <Paper elevation={1}>
                        <BidsTable
                            setDeleteBidId={setDeleteBidId}
                            deleteModalVisibilityControls={deleteModalVisibilityControls}
                            setDenyBidId={setDenyBidId}
                            denyModalVisibilityControls={denyModalVisibilityControls}
                            accountModalVisibilityControls={accountModalVisibilityControls}
                            fetchBid={fetchBid}
                            me={me}
                            refetchBids={refetchBids}
                            bids={bids}
                            paginationModel={paginationModel}
                            sortModel={sortModel}
                            filterModel={filterModel}
                            onPaginationModelChange={setPaginationModel}
                            onSortModelChange={setSortModel}
                            onFilterModelChange={setFilterModel}
                            rowCount={rowCount}
                            selectedCompanyValues={selectedCompanyValues}
                            onSelectedCompaniesChange={setSelectedCompanyValues}
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Fragment>
    );
}
